import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { youTubeParser } from 'utils/APIUtils';
import { isValidYouTubeOrEmpty, validate, Validator } from 'utils/Validation';
import { AuthDialog } from 'components/AuthDialog/AuthDialog';
import { changeNewSpot, clearAddSpot } from 'store/slices/newSpot';
import { Button, TextField, Typography } from '@mui/material';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { styled } from 'theme';
import Uploader, { UPLOADER_CATEGORY } from '../Uploader/Uploader';
import { FileUpload } from 'store/slices/file';
import Box from '@mui/material/Box';
import { useAutoSaveDraft } from 'hooks/useAutoSaveDraft';

const VideoIframe = styled('iframe')({
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: '0',
    top: '0',
});

const AddSpotStep3 = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { save } = useAutoSaveDraft();
    const newSpot = useAppSelector((state) => state.newSpot);
    const auth = useAppSelector((state) => state.auth);
    const { idUser } = auth.tokenData?.data || {};
    const { youTubeLink } = newSpot;

    const [errors, setErrors] = useState({
        youTubeLink: false,
    });
    const [showErrors, setShowErrors] = useState(false);

    const validators: Validator<string>[] = [
        {
            fieldName: 'youTubeLink',
            fieldValue: youTubeLink,
            isValid: isValidYouTubeOrEmpty,
        },
    ];

    const setFilesUploaded = (pictures: FileUpload[]) => {
        void dispatch(changeNewSpot({ pictures }));
    };

    useEffect(() => {
        validate(validators, setErrors);
    }, [youTubeLink]);

    const handleYouTubeLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        void dispatch(changeNewSpot({ youTubeLink: event.target.value }));
    };

    return (
        <AuthDialog
            title={'Create a New Spot'}
            onClose={() => {
                save(3);
                dispatch(clearAddSpot());
                history.push('/');
            }}
            onBack={() => {
                save(3);
                history.push('/add-spot/step-2');
            }}
        >
            <form>
                <Box>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        Photos{' '}
                        <Typography component="span" variant="body2" sx={{ opacity: 0.7, color: 'text.secondary' }}>
                            (optional)
                        </Typography>
                    </Typography>
                    <Uploader
                        idUser={idUser}
                        jwt={auth.jwt}
                        category={UPLOADER_CATEGORY.SPOT}
                        fileTypes={['image/png', 'image/jpeg']}
                        onFileUploaded={setFilesUploaded}
                    />

                    <Box mt={2}>
                        <TextField
                            fullWidth
                            label="YouTube link (optional)"
                            size="small"
                            type="text"
                            value={youTubeLink}
                            onChange={handleYouTubeLinkChange}
                            placeholder="https://youtu.be/..."
                            error={showErrors && errors.youTubeLink}
                        />
                        {showErrors && errors.youTubeLink && (
                            <Typography color="error" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                Please enter a valid YouTube link.
                            </Typography>
                        )}
                    </Box>

                    {youTubeLink && !errors.youTubeLink && (
                        <Box
                            sx={{
                                mt: 1.5,
                                position: 'relative',
                                width: '100%',
                                height: '0',
                                paddingBottom: '56.25%',
                                borderRadius: '8px',
                                overflow: 'hidden',
                            }}
                        >
                            <VideoIframe
                                frameBorder="0"
                                scrolling="no"
                                marginHeight={0}
                                marginWidth={0}
                                src={`https://www.youtube.com/embed/${youTubeParser(youTubeLink)}?autoplay=0`}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ textAlign: 'center', pt: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (validate(validators, setErrors)) {
                                save(3);
                                history.push('/add-spot/step-4');
                            } else {
                                setShowErrors(true);
                            }
                        }}
                    >
                        Continue
                    </Button>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '.85em' }}>
                        Next: privacy options
                    </Typography>
                </Box>
            </form>
        </AuthDialog>
    );
};

export default AddSpotStep3;
